/**
 * Path B (M28): an impromptu session, run without a section, waiting to be
 * bound to one. Two seats:
 *
 *   instructor  — told plainly that the class is running without a section
 *                 and what happens next; nothing else to do
 *   coordinator — after the class: build the section (the draft the class
 *                 created is one click away, or an existing section for a
 *                 tailgate's second date), then bind. Certify and award
 *                 open up after that; the gates never moved.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api.js';
import { useAuth } from '../auth/useAuth.js';
import Select from './Select.jsx';

export function BindSessionPanel({ session, onBound }) {
  const { profile } = useAuth();
  const canBind =
    (profile?.permissions?.includes('session.manage') && profile?.permissions?.includes('class.manage')) ?? false;
  const [sections, setSections] = useState([]);
  const [draft, setDraft] = useState(null);
  const [choice, setChoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!canBind) return;
    apiFetch('/api/v1/classes')
      .then((list) => {
        const all = list ?? [];
        setDraft(all.find((c) => c.id === session.class_id) ?? null);
        const published = all.filter((c) => c.status === 'published' && c.institution_id === session.institution_id);
        setSections(published);
        // The draft this class created, once published, is the natural choice.
        const own = published.find((c) => c.id === session.class_id);
        if (own) setChoice(own.id);
      })
      .catch(setError);
  }, [canBind, session.class_id, session.institution_id]);

  async function bind() {
    setError(null);
    setBusy(true);
    try {
      const result = await apiFetch(`/api/v1/sessions/${session.id}/bind`, {
        method: 'POST',
        body: JSON.stringify({ class_id: choice }),
      });
      onBound?.(result);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel bind" aria-labelledby="bind-heading">
      <h2 id="bind-heading">Running without a section</h2>
      {error && (
        <p className="banner banner--error" role="alert">
          {error.message}
        </p>
      )}
      <p>
        This class was started on the spot. Everything the room does is being captured — check-ins,
        attestations, attendance — but there is no section behind it yet, so it cannot be certified or
        awarded until a coordinator builds one and binds this session to it.
      </p>

      {canBind ? (
        <>
          <p className="muted">
            {draft && draft.status !== 'published' ? (
              <>
                The class created a draft section, <strong>{draft.title}</strong>.{' '}
                <Link to={`/sections/${draft.id}`}>Finish and publish it</Link> (CCL code, approved
                hours, the meeting), then come back here and bind. For another date of an existing
                section — a tailgate — bind to that section instead.
              </>
            ) : (
              <>Bind this session to the section it belongs to.</>
            )}
          </p>
          <div className="field-row">
            <Select
              label="Section"
              value={choice}
              onChange={setChoice}
              options={sections.map((c) => ({
                value: c.id,
                label: `${c.title}${c.course_prefix && c.course_number ? ` — ${c.course_prefix} ${c.course_number}${c.section_number ? `-${c.section_number}` : ''}` : ''}`,
              }))}
              placeholder={sections.length ? 'Choose a published section…' : 'No published sections yet'}
              required={false}
              optional={false}
              disabled={busy || !sections.length}
            />
          </div>
          <p className="toolbar">
            <button type="button" className="btn btn--primary btn--auto" onClick={bind} disabled={busy || !choice}>
              {busy ? 'Binding…' : 'Bind this session'}
            </button>
          </p>
          <p className="muted">
            Binding turns every attestation captured at the door into an enrolment on that section, as
            signed. Anyone already enrolled there keeps their existing enrolment.
          </p>
        </>
      ) : (
        <p className="muted">Nothing for you to do here — run the class as usual. The office takes it from there.</p>
      )}
    </section>
  );
}

export default BindSessionPanel;
